import { supabase } from './supabaseClient';
import { userCacheService } from './userCache';

// Helper function to get the week of the month (1-5)
const getWeekOfMonth = (date) => {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDayWeek = Math.ceil((firstDay.getDate() + firstDay.getDay()) / 7);
  const currentWeek = Math.ceil((date.getDate() + firstDay.getDay()) / 7);
  return currentWeek - firstDayWeek + 1;
};

// Helper function to get the start and end dates for a specific week of a month
const getWeekDates = (year, month, weekOfMonth) => {
  const firstDay = new Date(year, month, 1);
  const firstDayWeek = Math.ceil((firstDay.getDate() + firstDay.getDay()) / 7);
  const targetWeek = firstDayWeek + weekOfMonth - 1;
  
  const startDate = new Date(year, month, 1);
  startDate.setDate(startDate.getDate() + (targetWeek - 1) * 7 - firstDay.getDay());
  
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  
  return { startDate, endDate };
};

// Helper function to get donations for a specific week from cached data
const getDonationsForWeekFromData = (donations, year, month, weekOfMonth) => {
  const { startDate, endDate } = getWeekDates(year, month, weekOfMonth);
  const startDateStr = startDate.toISOString().split('T')[0];
  const endDateStr = endDate.toISOString().split('T')[0];
  
  return donations
    .filter(donation => {
      const donationDate = donation.date;
      return donationDate >= startDateStr && donationDate <= endDateStr;
    })
    .reduce((sum, donation) => sum + (parseFloat(donation.amount) || 0), 0);
};

// Helper function to get average donations for a specific week across multiple months
const getAverageDonationsForWeekFromData = (donations, weekOfMonth, monthsBack = 3) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  let totalDonations = 0;
  let monthCount = 0;
  
  for (let i = 1; i <= monthsBack; i++) {
    const targetMonth = currentMonth - i;
    const targetYear = currentYear;
    
    // Handle year rollover
    const actualYear = targetMonth < 0 ? targetYear - 1 : targetYear;
    const actualMonth = targetMonth < 0 ? 12 + targetMonth : targetMonth;
    
    const weekDonations = getDonationsForWeekFromData(donations, actualYear, actualMonth, weekOfMonth);
    totalDonations += weekDonations;
    monthCount++;
  }
  
  return monthCount > 0 ? totalDonations / monthCount : 0;
};

// Main function to calculate sophisticated donation trend - OPTIMIZED VERSION
export const calculateDonationTrend = async (providedCurrentWeekDonations = null) => {
  try {
    const organizationId = await userCacheService.getCurrentUserOrganizationId();
    if (!organizationId) {
      throw new Error('No organization ID found');
    }

    const now = new Date();
    const currentWeekOfMonth = getWeekOfMonth(now);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    console.log('📊 [DonationTrend] Analyzing week', currentWeekOfMonth, 'of month', currentMonth + 1);

    // SINGLE QUERY: Get all donations for the last 4 months (enough for trend analysis)
    const fourMonthsAgo = new Date();
    fourMonthsAgo.setMonth(fourMonthsAgo.getMonth() - 4);
    const fourMonthsAgoStr = fourMonthsAgo.toISOString().split('T')[0];
    
    console.log('📊 [DonationTrend] Fetching donations from:', fourMonthsAgoStr, 'for org:', organizationId);
    
    const { data: donations, error } = await supabase
      .from('donations')
      .select('amount, date')
      .eq('organization_id', organizationId)
      .gte('date', fourMonthsAgoStr)
      .order('date', { ascending: true });
    
    console.log('📊 [DonationTrend] Sample donations:', donations?.slice(-5));

    if (error) {
      console.error('Error fetching donations for trend analysis:', error);
      throw error;
    }

    console.log('📊 [DonationTrend] Fetched', donations?.length || 0, 'donations for analysis');

    // Use provided current week donations for consistency, or calculate if not provided
    let currentWeekDonations;
    let startOfWeek;
    let startOfWeekStr;
    
    if (providedCurrentWeekDonations !== null) {
      currentWeekDonations = providedCurrentWeekDonations;
      console.log('📊 [DonationTrend] Using provided current week donations:', currentWeekDonations);
    } else {
      // Calculate current week donations using the same logic as dashboard service
      const currentDate = new Date();
      const currentDayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
      startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);
      startOfWeekStr = startOfWeek.toISOString().split('T')[0];
      
      currentWeekDonations = donations
        .filter(d => d.date >= startOfWeekStr)
        .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      
      console.log('📊 [DonationTrend] Calculated current week donations:', currentWeekDonations);
    }
    
    // We still need startOfWeek for comparison calculations
    if (!startOfWeek) {
      const currentDate = new Date();
      const currentDayOfWeek = currentDate.getDay();
      startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);
      startOfWeekStr = startOfWeek.toISOString().split('T')[0];
    }

    // Calculate average of the last 3 weeks for comparison (simpler approach)
    const threeWeeksAgo = new Date(startOfWeek);
    threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
    const threeWeeksAgoStr = threeWeeksAgo.toISOString().split('T')[0];
    
    const lastThreeWeeksStr = startOfWeekStr; // Current week start
    const recentWeeksDonations = donations
      .filter(d => d.date >= threeWeeksAgoStr && d.date < lastThreeWeeksStr)
      .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
    
    const averageWeekDonations = recentWeeksDonations / 3; // Average of last 3 weeks

    // Calculate same week from last month using calendar weeks (not week of month)
    const lastWeekStart = new Date(startOfWeek);
    lastWeekStart.setDate(lastWeekStart.getDate() - 28); // Go back 4 weeks (approximately 1 month)
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);
    
    const lastWeekStartStr = lastWeekStart.toISOString().split('T')[0];
    const lastWeekEndStr = lastWeekEnd.toISOString().split('T')[0];
    
    const lastMonthWeekDonations = donations
      .filter(d => d.date >= lastWeekStartStr && d.date <= lastWeekEndStr)
      .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

    // Calculate trend percentages
    let trendVsAverage = null;
    let trendVsLastMonth = null;
    let canCalculateTrend = false;
    let trendDescription = '';

    // Always try to calculate trend, even if comparison values are 0
    if (lastMonthWeekDonations >= 0) {
      if (lastMonthWeekDonations > 0) {
        trendVsLastMonth = ((currentWeekDonations - lastMonthWeekDonations) / lastMonthWeekDonations) * 100;
      } else if (currentWeekDonations > 0) {
        trendVsLastMonth = 100; // 100% increase from 0
      } else {
        trendVsLastMonth = 0; // No change, both are 0
      }
      canCalculateTrend = true;
    }

    if (averageWeekDonations >= 0 && !canCalculateTrend) {
      if (averageWeekDonations > 0) {
        trendVsAverage = ((currentWeekDonations - averageWeekDonations) / averageWeekDonations) * 100;
      } else if (currentWeekDonations > 0) {
        trendVsAverage = 100; // 100% increase from 0
      } else {
        trendVsAverage = 0; // No change, both are 0
      }
      canCalculateTrend = true;
    }

    // Determine the primary trend to show
    let primaryTrend = null;
    let trendType = '';

    if (trendVsLastMonth !== null) {
      primaryTrend = trendVsLastMonth;
      trendType = 'lastMonth';
      trendDescription = `This Week: $${Math.round(currentWeekDonations).toLocaleString()} vs 4 Weeks Ago: $${Math.round(lastMonthWeekDonations).toLocaleString()}`;
    } else if (trendVsAverage !== null) {
      primaryTrend = trendVsAverage;
      trendType = 'average';
      trendDescription = `This Week: $${Math.round(currentWeekDonations).toLocaleString()} vs Recent Average: $${Math.round(averageWeekDonations).toLocaleString()}`;
    } else {
      // Fallback case - should rarely happen now
      primaryTrend = 0;
      trendType = 'current';
      trendDescription = `This Week: $${Math.round(currentWeekDonations).toLocaleString()}`;
      canCalculateTrend = true;
    }

    // Additional context for the trend - simplified
    const trendContext = canCalculateTrend 
      ? 'Weekly giving comparison' 
      : 'Insufficient data for trend analysis';

    const result = {
      currentWeekDonations,
      averageWeekDonations,
      lastMonthWeekDonations,
      currentWeekOfMonth,
      weekAnalyzed: currentWeekOfMonth,
      trendVsAverage,
      trendVsLastMonth,
      primaryTrend,
      trendType,
      canCalculateTrend,
      trendDescription,
      trendContext,
      debug: {
        currentYear,
        currentMonth: currentMonth + 1,
        totalDonationsAnalyzed: donations?.length || 0,
        startOfWeek: startOfWeekStr,
        currentWeekDonations,
        lastMonthWeekDonations
      }
    };

    console.log('📊 [DonationTrend] Analysis result:', result);
    return result;

  } catch (error) {
    console.error('Error calculating donation trend:', error);
    return {
      currentWeekDonations: 0,
      averageWeekDonations: 0,
      lastMonthWeekDonations: 0,
      currentWeekOfMonth: 0,
      trendVsAverage: null,
      trendVsLastMonth: null,
      primaryTrend: null,
      trendType: '',
      canCalculateTrend: false,
      trendDescription: 'Unable to calculate trend',
      trendContext: '',
      error: error.message
    };
  }
};

// Helper function to get weekly donation breakdown for current month - OPTIMIZED VERSION
export const getWeeklyDonationBreakdown = async () => {
  try {
    const organizationId = await userCacheService.getCurrentUserOrganizationId();
    if (!organizationId) {
      throw new Error('No organization ID found');
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const weeksInMonth = Math.ceil(daysInMonth / 7);

    // SINGLE QUERY: Get all donations for the last 4 months
    const fourMonthsAgo = new Date();
    fourMonthsAgo.setMonth(fourMonthsAgo.getMonth() - 4);
    const fourMonthsAgoStr = fourMonthsAgo.toISOString().split('T')[0];
    
    const { data: donations, error } = await supabase
      .from('donations')
      .select('amount, date')
      .eq('organization_id', organizationId)
      .gte('date', fourMonthsAgoStr)
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching donations for weekly breakdown:', error);
      return [];
    }

    const weeklyBreakdown = [];

    for (let week = 1; week <= weeksInMonth; week++) {
      const weekDonations = getDonationsForWeekFromData(donations, currentYear, currentMonth, week);
      const averageWeekDonations = getAverageDonationsForWeekFromData(donations, week);
      
      weeklyBreakdown.push({
        week,
        currentDonations: weekDonations,
        averageDonations: averageWeekDonations,
        trend: averageWeekDonations > 0 ? ((weekDonations - averageWeekDonations) / averageWeekDonations) * 100 : null
      });
    }

    return weeklyBreakdown;

  } catch (error) {
    console.error('Error getting weekly donation breakdown:', error);
    return [];
  }
}; 